import { useEffect, useState } from "react";
import { Plus, Wrench, CurrencyDollar, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { maintenanceApi } from "@/lib/api";

const SERVICE_TYPES = [
  { value: "oil_change", label: "Oil change" },
  { value: "tire_rotation", label: "Tire rotation" },
  { value: "brake_inspection", label: "Brake inspection" },
  { value: "tire_replacement", label: "Tire replacement" },
  { value: "engine_repair", label: "Engine repair" },
  { value: "transmission", label: "Transmission" },
  { value: "dot_inspection", label: "DOT inspection" },
  { value: "other", label: "Other" },
];

export default function AssetMaintenanceSection({ assetId }) {
  const [logs, setLogs] = useState([]);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: "oil_change", hours_at_service: "", cost_usd: "", notes: "" });

  const load = async () => {
    try {
      const rows = await maintenanceApi.list(assetId);
      setLogs(rows);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, [assetId]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await maintenanceApi.log(assetId, {
        type: form.type,
        hours_at_service: form.hours_at_service !== "" ? parseInt(form.hours_at_service) : undefined,
        cost_usd: form.cost_usd !== "" ? parseFloat(form.cost_usd) : undefined,
        notes: form.notes || undefined,
      });
      toast.success("Service logged");
      setShow(false);
      setForm({ type: "oil_change", hours_at_service: "", cost_usd: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log service");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-[#2a2a2a]" data-testid="maintenance-section">
      <div className="px-3 py-2 border-b border-[#2a2a2a] flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] font-bold flex items-center gap-1">
          <Wrench size={10} weight="bold" /> Service history ({logs.length})
        </div>
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] hover:bg-[#ff8c00]/10 px-2 py-1 font-bold flex items-center gap-1"
          data-testid="maintenance-add-btn"
        >
          <Plus size={10} weight="bold" /> Log Service
        </button>
      </div>
      {show && (
        <form onSubmit={submit} className="p-3 grid grid-cols-2 gap-2 border-b border-[#2a2a2a]" data-testid="maintenance-form">
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs"
            data-testid="maintenance-type-select"
          >
            {SERVICE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input
            type="number"
            value={form.hours_at_service}
            onChange={(e) => setForm({ ...form, hours_at_service: e.target.value })}
            placeholder="Hours at service (optional)"
            className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs font-mono"
            data-testid="maintenance-hours-input"
          />
          <input
            type="number"
            step="0.01"
            value={form.cost_usd}
            onChange={(e) => setForm({ ...form, cost_usd: e.target.value })}
            placeholder="Cost USD"
            className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs font-mono"
            data-testid="maintenance-cost-input"
          />
          <input
            type="text"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Notes"
            className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-2 py-1.5 text-xs"
            data-testid="maintenance-notes-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="col-span-2 bg-[#ff8c00] hover:bg-[#ff8c00]/90 disabled:opacity-50 text-black font-bold px-3 py-2 text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2"
            data-testid="maintenance-submit"
          >
            {busy ? <ArrowsClockwise size={11} className="animate-spin" /> : <CheckCircle size={11} weight="bold" />}
            Save Service Record
          </button>
        </form>
      )}
      <div className="max-h-[180px] overflow-y-auto">
        {logs.length === 0 ? (
          <div className="px-3 py-3 text-[10px] uppercase tracking-[0.2em] text-[#71717a]">No service records yet</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#1a1a1a]">
                {["Date", "Type", "Hours", "Cost", "By"].map((h) => (
                  <th key={h} className="text-left px-3 py-1.5 text-[9px] uppercase tracking-[0.2em] text-[#71717a] font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-[#2a2a2a]" data-testid={`maintenance-row-${l.id}`}>
                  <td className="px-3 py-1.5 font-mono text-[10px]">{new Date(l.performed_at).toLocaleDateString()}</td>
                  <td className="px-3 py-1.5">{SERVICE_TYPES.find((t) => t.value === l.type)?.label || l.type}</td>
                  <td className="px-3 py-1.5 font-mono">{l.hours_at_service ?? "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{l.cost_usd != null ? <span className="text-[#22c55e] flex items-center gap-0.5"><CurrencyDollar size={10} weight="bold" />{l.cost_usd.toFixed(2)}</span> : "—"}</td>
                  <td className="px-3 py-1.5 text-[10px] text-[#a1a1aa] truncate">{l.performed_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
